import type { ColumnSort, Header, SortDirection, SortingState, Table } from '@tanstack/react-table';
import {
  BaselineIcon,
  CalendarIcon,
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeOffIcon,
  HashIcon,
  ListChecksIcon,
  ListIcon,
  PinIcon,
  PinOffIcon,
  TextInitialIcon,
  XIcon,
} from 'lucide-react';
import * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/shared/components/ui/tooltip';
import { cn } from 'src/shared/lib/utils';
import type { Cell } from 'src/shared/types/data-grid';

function formatHeaderWithSmartSpacing(text: string): string {
  return (
    text
      // Split on special characters (underscore, dash, dot, etc.)
      .split(/[_\-.]+/)
      // Process each segment for camelCase
      .map((segment) => {
        // Split camelCase and handle transitions
        return (
          segment
            // Split on camelCase (lowercase followed by uppercase)
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            // Split on transitions from multiple uppercase to lowercase
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .trim()
        );
      })
      // Join segments and clean up
      .join(' ')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase()
  );
}

function getColumnVariant(variant?: Cell['variant']): {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
} | null {
  switch (variant) {
    case 'short-text':
      return { icon: BaselineIcon, label: 'Short text' };
    case 'long-text':
      return { icon: TextInitialIcon, label: 'Long text' };
    case 'number':
      return { icon: HashIcon, label: 'Number' };
    case 'select':
      return { icon: ListIcon, label: 'Select' };
    case 'multi-select':
      return { icon: ListChecksIcon, label: 'Multi-select' };
    case 'checkbox':
      return { icon: CheckSquareIcon, label: 'Checkbox' };
    case 'date':
      return { icon: CalendarIcon, label: 'Date' };
    default:
      return null;
  }
}

interface DataGridColumnHeaderProps<TData, TValue> extends React.ComponentProps<typeof DropdownMenuTrigger> {
  header: Header<TData, TValue>;
  table: Table<TData>;
  headerIcons?: boolean;
}

export function DataGridColumnHeader<TData, TValue>({
  header,
  table,
  headerIcons = false,
  className,
  onPointerDown,
  ...props
}: DataGridColumnHeaderProps<TData, TValue>) {
  const column = header.column;
  const originalLabel = column.columnDef.meta?.label
    ? column.columnDef.meta.label
    : typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id;

  const label = formatHeaderWithSmartSpacing(originalLabel);

  const isAnyColumnResizing = table.getState().columnSizingInfo.isResizingColumn;

  const cellVariant = column.columnDef.meta?.cell;
  const columnVariant = getColumnVariant(cellVariant?.variant);

  const pinnedPosition = column.getIsPinned();
  const isPinnedLeft = pinnedPosition === 'left';
  const isPinnedRight = pinnedPosition === 'right';

  const onSortingChange = React.useCallback(
    (direction: SortDirection) => {
      table.setSorting((prev: SortingState) => {
        const existingSortIndex = prev.findIndex((sort) => sort.id === column.id);
        const newSort: ColumnSort = {
          id: column.id,
          desc: direction === 'desc',
        };

        if (existingSortIndex >= 0) {
          const updated = [...prev];
          updated[existingSortIndex] = newSort;
          return updated;
        } else {
          return [...prev, newSort];
        }
      });
    },
    [column.id, table]
  );

  const onSortRemove = React.useCallback(() => {
    table.setSorting((prev: SortingState) => prev.filter((sort) => sort.id !== column.id));
  }, [column.id, table]);

  const onLeftPin = React.useCallback(() => {
    column.pin('left');
  }, [column]);

  const onRightPin = React.useCallback(() => {
    column.pin('right');
  }, [column]);

  const onUnpin = React.useCallback(() => {
    column.pin(false);
  }, [column]);

  const onTriggerPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      onPointerDown?.(event);
      if (event.defaultPrevented) return;

      if (event.button !== 0) {
        return;
      }
      table.options.meta?.onColumnClick?.(column.id);
    },
    [table.options.meta, column.id, onPointerDown]
  );

  const onDivPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;

      if (event.button !== 0) {
        return;
      }
      table.options.meta?.onColumnClick?.(column.id);
    },
    [table.options.meta, column.id]
  );

  // Check if there are any dropdown options available
  const hasDropdownOptions = column.getCanSort() || column.getCanPin() || column.getCanHide();

  return (
    <>
      {hasDropdownOptions ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'flex size-full items-center justify-between gap-2 p-2 text-sm hover:bg-neutral-100/40 data-[state=open]:bg-neutral-100/40 [&_svg]:size-4 dark:hover:bg-neutral-800/40 dark:data-[state=open]:bg-neutral-800/40',
              isAnyColumnResizing && 'pointer-events-none',
              className
            )}
            onPointerDown={onTriggerPointerDown}
            {...props}
          >
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {headerIcons && columnVariant && (
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <columnVariant.icon className="size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{columnVariant.label}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <span className="truncate uppercase font-medium text-neutral-500 dark:text-neutral-300">{label}</span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{originalLabel}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <ChevronDownIcon className="shrink-0 text-neutral-500 dark:text-neutral-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" sideOffset={0} className="w-60">
            {column.getCanSort() && (
              <>
                <DropdownMenuCheckboxItem
                  className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                  checked={column.getIsSorted() === 'asc'}
                  onClick={() => onSortingChange('asc')}
                >
                  <ChevronUpIcon />
                  Sort asc
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                  checked={column.getIsSorted() === 'desc'}
                  onClick={() => onSortingChange('desc')}
                >
                  <ChevronDownIcon />
                  Sort desc
                </DropdownMenuCheckboxItem>
                {column.getIsSorted() && (
                  <DropdownMenuItem onClick={onSortRemove}>
                    <XIcon />
                    Remove sort
                  </DropdownMenuItem>
                )}
              </>
            )}
            {column.getCanPin() && (
              <>
                {column.getCanSort() && <DropdownMenuSeparator />}

                {isPinnedLeft ? (
                  <DropdownMenuItem
                    className="[&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                    onClick={onUnpin}
                  >
                    <PinOffIcon />
                    Unpin from left
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="[&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                    onClick={onLeftPin}
                  >
                    <PinIcon />
                    Pin to left
                  </DropdownMenuItem>
                )}
                {isPinnedRight ? (
                  <DropdownMenuItem
                    className="[&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                    onClick={onUnpin}
                  >
                    <PinOffIcon />
                    Unpin from right
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="[&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                    onClick={onRightPin}
                  >
                    <PinIcon />
                    Pin to right
                  </DropdownMenuItem>
                )}
              </>
            )}
            {column.getCanHide() && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-neutral-500 dark:[&_svg]:text-neutral-400"
                  checked={!column.getIsVisible()}
                  onClick={() => column.toggleVisibility(false)}
                >
                  <EyeOffIcon />
                  Hide column
                </DropdownMenuCheckboxItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div
          className={cn(
            'flex size-full items-center justify-between gap-2 p-2 text-sm',
            isAnyColumnResizing && 'pointer-events-none',
            className
          )}
          onPointerDown={onDivPointerDown}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {headerIcons && columnVariant && (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <columnVariant.icon className="size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400" />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{columnVariant.label}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <span className="truncate uppercase font-medium text-neutral-500 dark:text-neutral-300">{label}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{originalLabel}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      {header.column.getCanResize() && <DataGridColumnResizer header={header} table={table} label={originalLabel} />}
    </>
  );
}

const DataGridColumnResizer = React.memo(DataGridColumnResizerImpl, (prev, next) => {
  const prevColumn = prev.header.column;
  const nextColumn = next.header.column;

  if (prevColumn.getIsResizing() !== nextColumn.getIsResizing() || prevColumn.getSize() !== nextColumn.getSize()) {
    return false;
  }

  if (prev.label !== next.label) return false;

  return true;
}) as typeof DataGridColumnResizerImpl;

interface DataGridColumnResizerProps<TData, TValue> extends DataGridColumnHeaderProps<TData, TValue> {
  label: string;
}

function DataGridColumnResizerImpl<TData, TValue>({ header, table, label }: DataGridColumnResizerProps<TData, TValue>) {
  const defaultColumnDef = table._getDefaultColumnDef();

  const onDoubleClick = React.useCallback(() => {
    header.column.resetSize();
  }, [header.column]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={header.column.getSize()}
      aria-valuemin={defaultColumnDef.minSize}
      aria-valuemax={defaultColumnDef.maxSize}
      tabIndex={0}
      className={cn(
        "after:-translate-x-1/2 -right-px absolute top-0 z-50 h-full w-0.5 cursor-ew-resize touch-none select-none bg-neutral-200 transition-opacity after:absolute after:inset-y-0 after:left-1/2 after:h-full after:w-[18px] after:content-[''] hover:bg-neutral-900 focus:bg-neutral-900 focus:outline-none dark:bg-neutral-800 dark:hover:bg-neutral-50 dark:focus:bg-neutral-50",
        header.column.getIsResizing() ? 'bg-neutral-900 dark:bg-neutral-50' : 'opacity-0 hover:opacity-100'
      )}
      onDoubleClick={onDoubleClick}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
    />
  );
}
